import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Briefcase } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function AdminInternships() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { title: "", description: "", duration: "", skills: "", is_active: true };

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get("/admin/internships");
    setItems(r.data || []);
  };

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/admin/internships/${editing.id}`, editing);
      } else {
        await api.post("/admin/internships", editing);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this internship?")) return;
    await api.delete(`/admin/internships/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between">
        <h1 className="heading text-2xl font-bold">Internship Programs</h1>
        <button onClick={() => setEditing(empty)} className="bg-brand hover:bg-[#172b66] text-white font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-1" data-testid="new-internship-button">
          <Plus size={16} /> New Program
        </button>
      </div>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="p-10 rounded-xl border border-dashed border-slate-300 text-center col-span-full">
            <Briefcase size={36} className="mx-auto text-slate-300" />
            <div className="mt-3 text-slate-500">No internships yet.</div>
          </div>
        )}
        {items.map((i) => (
          <div key={i.id} className="p-5 rounded-xl border border-slate-200 bg-white" data-testid={`admin-intern-${i.id}`}>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#F59E0B] font-bold">{i.duration}</div>
            <h3 className="heading text-lg font-bold mt-1">{i.title}</h3>
            <p className="text-sm text-slate-600 mt-2 line-clamp-3">{i.description}</p>
            <div className="mt-3 flex items-center gap-2 text-xs">
              <span className={`px-2 py-1 rounded font-bold ${i.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {i.is_active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setEditing(i)} className="text-slate-700 hover:bg-slate-100 p-2 rounded" data-testid={`edit-intern-${i.id}`}><Edit size={14} /></button>
              <button onClick={() => del(i.id)} className="text-red-600 hover:bg-red-50 p-2 rounded"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit" : "Create"} Internship</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Inp label="Title" v={editing.title} on={(v) => setEditing({ ...editing, title: v })} test="intern-title-input" />
              <Area label="Description" v={editing.description} on={(v) => setEditing({ ...editing, description: v })} test="intern-desc-input" />
              <Inp label="Duration (e.g., 2 months)" v={editing.duration} on={(v) => setEditing({ ...editing, duration: v })} test="intern-duration-input" />
              <Inp label="Skills" v={editing.skills || ""} on={(v) => setEditing({ ...editing, skills: v })} test="intern-skills-input" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="accent-brand" />
                Active (visible publicly)
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm">Cancel</button>
            <button onClick={save} className="bg-brand text-white font-semibold rounded-lg px-5 py-2 text-sm" data-testid="intern-save-button">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Inp({ label, v, on, test }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <input value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-brand outline-none" data-testid={test} />
    </div>
  );
}
function Area({ label, v, on, test }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <textarea rows={4} value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-brand outline-none resize-none" data-testid={test} />
    </div>
  );
}
